import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import type { HelpContextId } from '../../help/contexts';
import { DialogHelp } from '../../help/DialogHelp';

export function ReasonDialog({
  title,
  actionLabel,
  onCancel,
  onSubmit,
  submitting = false,
  error = null,
  context,
  disabled = false,
  cancelLabel = 'Close',
  help,
}: {
  context?: ReactNode;
  cancelLabel?: string;
  disabled?: boolean;
  title: string;
  actionLabel: string;
  onCancel: () => void;
  onSubmit: (reason: string) => void | Promise<void>;
  submitting?: boolean;
  error?: string | null;
  /** The dialog's "?" (src/help/contexts.ts); omitted = no "?". */
  help?: HelpContextId;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const valid = trimmed.length >= 3 && trimmed.length <= 500;
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`${styles.dialog} ${styles.compact}`}
      >
        <header className={styles.header}>
          <h2>{title}</h2>
          {help && <DialogHelp context={help} title={title} />}
        </header>
        <div className={styles.body}>
          {context}
          <label className={styles.field}>
            {t('payments.reasonDialog.reasonLabel')}
            <textarea
              aria-label={t('payments.reasonDialog.reasonLabel')}
              maxLength={500}
              rows={4}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {!valid && reason.length > 0 && (
            <div role="alert" className={styles.error}>
              {t('payments.reasonDialog.lengthError')}
            </div>
          )}
          {error && (
            <div role="alert" className={styles.error}>
              {error}
            </div>
          )}
          <div className={styles.actions}>
            <Button variant="secondary" size="sm" disabled={submitting} onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={!valid || submitting || disabled}
              onClick={() => onSubmit(trimmed)}
            >
              {submitting ? 'Saving…' : actionLabel}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
